import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { getPlayerHomeDashboard } from "../../../services/playerEcosystemService";
import { currentUserCanHostTournament } from "../../../services/organizationService";
import { getPlayerRankingProfile } from "../../../services/rankingService";
import { listBrowseCompetitions } from "../../../services/tournamentBrowseService";
import { listNotifications } from "../../../services/notificationService";
import { pickPrimaryResumableRoom, roomRecentToResumeRow } from "../../../utils/roomRecents";
import { resolveHomeGreetingName } from "../../../domain/auth/rallyAllyAuthEmail";
import { greetingLine, heroHeadline } from "../../match/lobby/utils/homeTodayCopy";
import { useMyPlayerProfile } from "../../profile/hooks/useMyPlayerProfile";
import { useActiveSport } from "../../../sports/ActiveSportContext";
import {
  composeNextActionCard,
  composeTodaysJourney,
} from "../domain/todaysJourney";
import { toUserFacingErrorMessage } from "../../../utils/userFacingError";

const HOME_CACHE_TTL_MS = 45_000;

/** @type {{ key: string|null, at: number, dash: object|null, ranking: object|null, suggested: object[], canHostTournament: boolean }} */
let homeCache = {
  key: null,
  at: 0,
  dash: null,
  ranking: null,
  suggested: [],
  canHostTournament: false,
};

function homeCacheKey(uid, sportId) {
  return `${uid || ""}:${sportId || "default"}`;
}

function readHomeCache(uid, sportId) {
  const key = homeCacheKey(uid, sportId);
  if (!uid || homeCache.key !== key) return null;
  if (Date.now() - homeCache.at > HOME_CACHE_TTL_MS) return null;
  return homeCache;
}

function writeHomeCache(uid, sportId, partial) {
  homeCache = {
    key: homeCacheKey(uid, sportId),
    at: Date.now(),
    dash: partial.dash !== undefined ? partial.dash : homeCache.dash,
    ranking: partial.ranking !== undefined ? partial.ranking : homeCache.ranking,
    suggested: partial.suggested !== undefined ? partial.suggested : homeCache.suggested,
    canHostTournament:
      partial.canHostTournament !== undefined
        ? partial.canHostTournament
        : homeCache.canHostTournament,
  };
}

/**
 * Orchestrates existing services for Player Home — progressive paint + short session cache.
 * Season KPIs, recent form, and ranking follow Global Active Sport.
 */
export function usePlayerHomeData() {
  const { uid, user } = useAuth();
  const { sportId } = useActiveSport();
  const { view: profileView } = useMyPlayerProfile();
  const cached = uid ? readHomeCache(uid, sportId) : null;
  const [dash, setDash] = useState(() => cached?.dash || null);
  const [ranking, setRanking] = useState(() => cached?.ranking || null);
  const [suggested, setSuggested] = useState(() => cached?.suggested || []);
  const [canHostTournament, setCanHostTournament] = useState(
    () => Boolean(cached?.canHostTournament)
  );
  const [loading, setLoading] = useState(() => !cached?.dash);
  const [enriching, setEnriching] = useState(false);
  const [error, setError] = useState(null);

  const resumeRoom = useMemo(() => {
    if (!uid) return null;
    return roomRecentToResumeRow(pickPrimaryResumableRoom(uid));
  }, [uid]);

  const loadSecondary = useCallback(
    async (playerId) => {
      if (!playerId) {
        setRanking(null);
        setSuggested([]);
        return;
      }
      setEnriching(true);
      try {
        const [rankSettled, browseSettled] = await Promise.allSettled([
          getPlayerRankingProfile(playerId, { sportId }),
          listBrowseCompetitions({ status: "upcoming", query: "", sportId }),
        ]);
        const nextRanking = rankSettled.status === "fulfilled" ? rankSettled.value : null;
        const nextSuggested =
          browseSettled.status === "fulfilled" && Array.isArray(browseSettled.value)
            ? browseSettled.value.slice(0, 3)
            : [];
        setRanking(nextRanking);
        setSuggested(nextSuggested);
        if (uid) {
          writeHomeCache(uid, sportId, { ranking: nextRanking, suggested: nextSuggested });
        }
      } finally {
        setEnriching(false);
      }
    },
    [uid, sportId]
  );

  const reload = useCallback(async () => {
    if (!uid) return;
    const warm = readHomeCache(uid, sportId);
    if (warm?.dash) {
      setDash(warm.dash);
      setRanking(warm.ranking);
      setSuggested(warm.suggested || []);
      setCanHostTournament(Boolean(warm.canHostTournament));
      setLoading(false);
    } else {
      setLoading(true);
      setDash(null);
      setRanking(null);
    }
    setError(null);
    try {
      const [next, hostCapable] = await Promise.all([
        getPlayerHomeDashboard(uid, { sportFilter: sportId }),
        currentUserCanHostTournament().catch(() => false),
      ]);
      setDash(next);
      setCanHostTournament(Boolean(hostCapable));
      writeHomeCache(uid, sportId, { dash: next, canHostTournament: Boolean(hostCapable) });
      setLoading(false);
      void loadSecondary(next?.profile?.playerId);
    } catch (err) {
      setError(
        toUserFacingErrorMessage(err, {
          context: "home",
          fallback: "Could not load player home.",
        })
      );
      if (!warm?.dash) setDash(null);
      if (!warm?.dash) setCanHostTournament(false);
      setLoading(false);
    }
  }, [uid, sportId, loadSecondary]);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setDash(null);
      setRanking(null);
      setSuggested([]);
      setCanHostTournament(false);
      return undefined;
    }
    void reload();
    return undefined;
  }, [reload, uid]);

  const notifications = useMemo(() => listNotifications(), [dash, loading]);

  const displayName = resolveHomeGreetingName(dash?.profile || profileView, user);
  const greet = greetingLine(loading && !displayName ? undefined : displayName);
  const headline = heroHeadline({ hasResumableSession: Boolean(resumeRoom?.room) });

  const journey = useMemo(
    () =>
      composeTodaysJourney({
        resumeRoom,
        registrations: dash?.registrations,
        ranking,
        achievements: dash?.achievements,
        recentMatches: dash?.recentMatches,
        stats: dash?.stats,
        suggestedCompetitions: suggested,
        notifications,
      }),
    [resumeRoom, dash, ranking, suggested, notifications]
  );

  const nextCard = useMemo(
    () =>
      composeNextActionCard({
        resumeRoom,
        registrations: dash?.registrations,
        displayName,
      }),
    [resumeRoom, dash, displayName]
  );

  return {
    uid,
    dash,
    ranking,
    suggested,
    canHostTournament,
    loading,
    enriching,
    error,
    reload,
    greet,
    headline,
    journey,
    nextCard,
    notifications,
    resumeRoom,
    sportId,
  };
}
